import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, Typography, Divider,
  IconButton, MenuItem, Select, FormControl, InputLabel,
  CircularProgress,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { AuthContext } from '../../context/Auth/AuthContext';
import api from '../../services/api';
import { toast } from 'react-toastify';
import toastError from '../../errors/toastError';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    borderRadius: 12,
    minWidth: 420,
  },
  dialogTitleRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 8,
  },
  sectionLabel: {
    fontSize: '0.7rem',
    color: '#9b9b9b',
    textTransform: 'uppercase',
    fontWeight: 700,
    letterSpacing: '0.05em',
    marginTop: 8,
    marginBottom: 4,
  },
  inputField: {
    '& .MuiOutlinedInput-root': { borderRadius: 8, fontSize: '0.875rem' },
    '& .MuiInputLabel-root': { fontSize: '0.875rem' },
  },
  saveBtn: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.85rem',
    minWidth: 100,
  },
  cancelBtn: {
    borderRadius: 8,
    textTransform: 'none',
    fontSize: '0.85rem',
    color: '#6b6b6b',
  },
  required: {
    color: theme.palette.error.main,
    marginLeft: 2,
  },
}));

const KanbanCreateLeadModal = ({ open, onClose, lanes = [], defaultLaneId = null, onCreated }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [form, setForm] = useState({
    name: '',
    number: '',
    email: '',
    empresa: '',
    servico: '',
    valor: '',
    laneId: defaultLaneId || '',
    whatsappId: '',
  });
  const [whatsapps, setWhatsapps] = useState([]);
  const [saving, setSaving] = useState(false);

  // Preenche a etapa ao abrir
  useEffect(() => {
    if (open) {
      setForm(prev => ({ ...prev, laneId: defaultLaneId || '' }));
    }
  }, [open, defaultLaneId]);

  // Carrega conexões WhatsApp disponíveis
  useEffect(() => {
    if (!open) return;
    api.get('/whatsapp', { params: { companyId: user.companyId, session: 0 } })
      .then(({ data }) => {
        setWhatsapps(data || []);
        if (data && data.length > 0) {
          setForm(prev => ({ ...prev, whatsappId: data[0].id }));
        }
      })
      .catch(err => console.error(err));
  }, [open, user.companyId]);

  const handleChange = field => e => {
    const value = e && e.target ? e.target.value : '';
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Nome é obrigatório'); return; }
    if (!form.number.trim()) { toast.error('Telefone é obrigatório'); return; }
    if (!form.whatsappId) { toast.error('Selecione uma conexão'); return; }

    setSaving(true);
    try {
      // 1. Monta extraInfo
      const extraInfo = [];
      if (form.empresa) extraInfo.push({ name: 'empresa', value: form.empresa });
      if (form.servico) extraInfo.push({ name: 'servico', value: form.servico });
      if (form.valor) extraInfo.push({ name: 'valor', value: form.valor });

      // 2. Cria contato (passa stageId para o webhook contact.created incluir a etapa)
      const cleanNumber = form.number.replace(/\D/g, '');
      const stageId = form.laneId && form.laneId !== 'lane0' && form.laneId !== '' ? form.laneId : undefined;
      const { data: contact } = await api.post('/contacts', {
        name: form.name,
        number: cleanNumber,
        email: form.email || undefined,
        extraInfo,
        stageId,
      });

      // 3. Cria ticket (inicia como "pending" para automação de boas-vindas disparar)
      const { data: ticket } = await api.post('/tickets', {
        contactId: contact.id,
        status: 'pending',
        userId: user.id,
        whatsappId: form.whatsappId,
      });

      // 4. Adiciona à etapa (tag kanban) se não for "Novos Leads"
      if (form.laneId && form.laneId !== 'lane0' && form.laneId !== '') {
        await api.put(`/ticket-tags/${ticket.id}/${form.laneId}`);
      }

      toast.success('Lead criado com sucesso!');
      onCreated && onCreated();
      handleClose();
    } catch (err) {
      // toastError traduz códigos do backend (backendErrors.*) para mensagens claras
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setForm({ name: '', number: '', email: '', empresa: '', servico: '', valor: '', laneId: '', whatsappId: '' });
    onClose();
  };

  // Filtra lanes para não mostrar "lane0" com nome padrão
  const laneOptions = lanes.filter(l => l.id !== 'lane0');

  return (
    <Dialog open={open} onClose={handleClose} classes={{ paper: classes.dialogPaper }}>
      <DialogTitle disableTypography className={classes.dialogTitleRoot}>
        <Typography variant="h6" style={{ fontSize: '1rem', fontWeight: 700 }}>
          Criar Lead
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container spacing={2} style={{ marginTop: 2 }}>

          {/* Contato */}
          <Grid item xs={12}>
            <Typography className={classes.sectionLabel}>Contato</Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={<>Nome <span className={classes.required}>*</span></>}
              fullWidth value={form.name}
              onChange={handleChange('name')}
              variant="outlined" size="small"
              className={classes.inputField}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={<>Telefone <span className={classes.required}>*</span></>}
              fullWidth value={form.number}
              onChange={handleChange('number')}
              variant="outlined" size="small"
              className={classes.inputField}
              placeholder="5511999999999"
              helperText="Apenas números com DDI"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="E-mail"
              fullWidth value={form.email}
              onChange={handleChange('email')}
              variant="outlined" size="small"
              className={classes.inputField}
              type="email"
            />
          </Grid>

          {/* Negócio */}
          <Grid item xs={12}>
            <Typography className={classes.sectionLabel}>Negócio</Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Empresa"
              fullWidth value={form.empresa}
              onChange={handleChange('empresa')}
              variant="outlined" size="small"
              className={classes.inputField}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Serviço / Produto"
              fullWidth value={form.servico}
              onChange={handleChange('servico')}
              variant="outlined" size="small"
              className={classes.inputField}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Valor Negociado (R$)"
              type="number"
              fullWidth value={form.valor}
              onChange={handleChange('valor')}
              variant="outlined" size="small"
              className={classes.inputField}
            />
          </Grid>

          {/* Configurações */}
          <Grid item xs={12}>
            <Typography className={classes.sectionLabel}>Configurações</Typography>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth variant="outlined" size="small" className={classes.inputField}>
              <InputLabel>Etapa</InputLabel>
              <Select
                value={form.laneId}
                onChange={handleChange('laneId')}
                label="Etapa"
                style={{ borderRadius: 8, fontSize: '0.875rem' }}
              >
                <MenuItem value="">Novos Leads</MenuItem>
                {laneOptions.map(lane => (
                  <MenuItem key={lane.id} value={lane.id}>{lane.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth variant="outlined" size="small" className={classes.inputField}>
              <InputLabel>Conexão <span className={classes.required}>*</span></InputLabel>
              <Select
                value={form.whatsappId}
                onChange={handleChange('whatsappId')}
                label="Conexão *"
                style={{ borderRadius: 8, fontSize: '0.875rem' }}
              >
                {whatsapps.map(w => (
                  <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions style={{ padding: '12px 24px' }}>
        <Button onClick={handleClose} className={classes.cancelBtn}>Cancelar</Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          className={classes.saveBtn}
          disabled={saving}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Criar Lead'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KanbanCreateLeadModal;
